import React, { useMemo, useState } from 'react';
import { sortBySearchPriority } from '../../utils/searchPriority';

const qtyText = (q, unit) =>
  `${Number(q || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}${unit ? ` ${unit}` : ''}`;

/**
 * Fold a duplicate name into the real item (spec §9.5). Chips first — the server's closest
 * names of the same unit family — then a ranked search with arrow keys and Enter, the
 * MenuItemPicker way. The merge itself is the existing merge engine, run by the caller;
 * this only picks, previews and confirms.
 *
 * An item of another unit family is never offered (the engine refuses pcs into kg), except
 * an exact name match, shown greyed as "different unit" so the owner sees why it is not there.
 */
export default function IngredientMergePicker({ card, ingredients, busy = false, onConfirm, onCancel }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [target, setTarget] = useState(null);
  const [confirmTypeChange, setConfirmTypeChange] = useState(false);

  const byId = useMemo(() => new Map((ingredients || []).map((i) => [Number(i.item_id), i])), [ingredients]);
  const pickable = useMemo(
    () => (ingredients || []).filter((i) => i.item_id !== card.item_id && i.unit_family === card.unit_family),
    [ingredients, card]
  );
  const chips = useMemo(
    () => (card.merge_suggestions || []).map((id) => byId.get(Number(id))).filter(Boolean).slice(0, 3),
    [card, byId]
  );
  const matches = useMemo(() => {
    if (!query.trim()) return [];
    return sortBySearchPriority(pickable, query, (i) => i.name, { limit: 8 });
  }, [pickable, query]);
  const exactOtherFamily = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return (ingredients || []).filter((i) => i.item_id !== card.item_id && i.unit_family !== card.unit_family
      && String(i.name).trim().toLowerCase() === q);
  }, [ingredients, card, query]);

  const choose = (i) => {
    setTarget(i);
    setConfirmTypeChange(false);
    setQuery('');
    setOpen(false);
    setActiveIdx(0);
  };
  const onKeyDown = (e) => {
    if (matches.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(matches[activeIdx]); }
    else if (e.key === 'Escape') setOpen(false);
  };

  const typeChange = target && Boolean(target.is_non_consumable) !== Boolean(card.is_non_consumable);
  const confirm = () => {
    if (!target) return;
    // Consumable <-> non-consumable: the stock changes kind, so it is asked once more.
    if (typeChange && !confirmTypeChange) { setConfirmTypeChange(true); return; }
    onConfirm(target);
  };

  return (
    <div className="nir-merge">
      {chips.length > 0 && (
        <div className="um-chips">
          {chips.map((i) => (
            <button key={i.item_id} type="button" className="um-chip" disabled={busy} onClick={() => choose(i)}>
              {i.name} · {i.unit}{i.in_recipe ? ' · in a recipe' : ''}
            </button>
          ))}
        </div>
      )}
      <div className="um-search-wrap">
        <input
          type="text" value={query} disabled={busy} placeholder="Search ingredients…" aria-label="Search ingredients"
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(0); }}
          onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
        />
        {open && (matches.length > 0 || exactOtherFamily.length > 0) && (
          <div className="um-dropdown" role="listbox">
            {matches.map((i, n) => (
              <div key={i.item_id} role="option" aria-selected={n === activeIdx}
                className={`um-dropdown-item nir-merge-result${n === activeIdx ? ' nim-active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); choose(i); }}>
                <span>{i.name}</span>
                <span className="nir-merge-result-meta">
                  {[i.unit, i.category, i.in_recipe ? 'in a recipe' : null].filter(Boolean).join(' · ')}
                </span>
              </div>
            ))}
            {exactOtherFamily.map((i) => (
              <div key={`x${i.item_id}`} role="option" aria-selected={false} aria-disabled="true"
                className="um-dropdown-item nir-merge-result nir-merge-muted">
                <span>{i.name}</span>
                <span className="nir-merge-result-meta">{i.unit} · different unit</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {target && (
        <div className="nir-merge-preview">
          <strong>{card.name} → {target.name}</strong>
          <p>
            Moves {qtyText(card.current_stock, card.unit)} of stock and this month&apos;s {card.bills} bill{card.bills === 1 ? '' : 's'}.
            {' '}Future bills with this name will go to {target.name}.
          </p>
          {typeChange && (
            <p className="nir-merge-warn">
              {card.name}&apos;s stock joins a {target.is_non_consumable ? 'non-consumable' : 'consumable'} item.
            </p>
          )}
          <div className="nim-preview-actions">
            <button type="button" className="um-skip-btn" disabled={busy} onClick={() => { setTarget(null); onCancel(); }}>Cancel</button>
            <button type="button" className="um-primary-btn nim-confirm" disabled={busy} onClick={confirm}>
              {typeChange && confirmTypeChange ? 'Yes, merge' : 'Confirm merge'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
